// React and utilities
import React, { useMemo, useState, useRef } from "react";
import Image from "next/image";

// Components
import { Button, Heading } from "@/shared/components/ui";
import TableRow from "./TableRow";
import IllnessSubSection from "./IllnessSubsection";
import SectionHeading from "./SectionHeading";

// Icons and images
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight, faWarning } from "@fortawesome/pro-solid-svg-icons";
import OneInThree from "@/shared/media/images/1in3.webp";
import EverySixSeconds from "@/shared/media/images/every6seconds.webp";
import TelehealthIcon from "@/shared/media/icons/policy/Telehealth.svg";
import TelehealthIconRebrand from "@/shared/media/icons/policy/Telehealth-rebrand.svg";
import ShotVaccine from "@/shared/media/icons/policy/Shot-Vaccine.svg";
import ShotVaccineRebrand from "@/shared/media/icons/policy/Shot-Vaccine-rebrand.svg";

// Data & Hooks
import { getGeneral, getIllnessSubSections, coverageExclusions, IncludesItem, SectionData, getAccident, WaitingPeriods, WAITING_PERIODS_TITLE } from "./coverageData";
import { PartialPet } from "@/shared/types/Quote.interface";
import { UIUtils } from "@/shared/utils/UIUtils";
import Strings from "@/shared/utils/Strings.constants";
import { cn } from "@/shared/utils";
import { useBreedSpecificBadge } from "@/shared/hooks/useBreedSpecificBadge";
import { useThemeContext } from "@/shared/contexts/ThemeProvider";

type TabOption = "covered" | "notCovered";
interface FullCoverageDetailsContentProps {
    initialTab?: "covered" | "notCovered";
    activePet?: PartialPet;
    policies?: PartialPet[];
    isMultiPetLinked?: boolean;
    showTelehealth?: boolean;
    samplePolicyUrl?: string;
    preExistingConditions?: string;
    waitingPeriods?: WaitingPeriods;
    isDentalCovered?: boolean;
    isMicrochippingCovered?: boolean;
    showCompellingStats?: boolean;
    hasIllnessCoverage?: boolean;
    showBreedSpecificConditions?: boolean;
    hasAnyBreedConditions?: boolean;
}

const FullCoverageDetailsContent: React.FC<FullCoverageDetailsContentProps> = props => {
    const {
        initialTab = "covered",
        activePet,
        policies,
        isMultiPetLinked,
        showTelehealth,
        samplePolicyUrl,
        preExistingConditions,
        waitingPeriods,
        isDentalCovered,
        isMicrochippingCovered,
        showCompellingStats,
        showBreedSpecificConditions,
        hasAnyBreedConditions,
        hasIllnessCoverage
    } = props;

    const { showRebrandTheme } = useThemeContext().theme;
    const illnessCoverageEnabled = hasIllnessCoverage ?? activePet?.coverageSettings?.coverages?.some(coverage => coverage.type === "illness") ?? false;

    const dialogRef = useRef<HTMLDivElement>(null);
    const illnessSubSections = getIllnessSubSections(undefined, showRebrandTheme);

    const { getBreedSpecificBadge, getDisplayBreedName } = useBreedSpecificBadge({
        isMultiPetLinked,
        policies: policies,
        activePetBreed: activePet?.breedName,
        enabled: showBreedSpecificConditions
    });

    const [activeTab, setActiveTab] = useState<"covered" | "notCovered">(initialTab);
    const isCoveredTab = activeTab === "covered";

    const targetBreed = useMemo(() => {
        return isMultiPetLinked ? policies?.[0]?.breedName : activePet?.breedName;
    }, [isMultiPetLinked, policies, activePet?.breedName]);

    const accident = getAccident(targetBreed, showBreedSpecificConditions, showRebrandTheme);
    const general = useMemo(
        () => getGeneral(illnessCoverageEnabled, targetBreed, showBreedSpecificConditions, showRebrandTheme),
        [illnessCoverageEnabled, targetBreed, showBreedSpecificConditions, showRebrandTheme]
    );

    if (!illnessCoverageEnabled || !isMicrochippingCovered) {
        const treatmentsItem = general.includes.find(item => item.title === Strings.PTZ_US.COVERAGE_ACCIDENT.TREATMENTS.title);
        if (treatmentsItem && "description" in treatmentsItem && treatmentsItem.description) {
            treatmentsItem.description = treatmentsItem.description.replace("microchipping, ", "");
        }
    }

    const combinedIncludes: IncludesItem[] = useMemo(() => {
        return illnessCoverageEnabled ? general.includes : [...general.includes, ...accident.includes];
    }, [accident.includes, general.includes, illnessCoverageEnabled]);

    const handleSetActiveTab = (tab: TabOption) => {
        setActiveTab(tab);
        if (dialogRef.current) {
            UIUtils.scrollToTop(500, 50, dialogRef.current);
        }
    };

    // Rendered when a per-config waiting period (e.g. Canada) is provided: at the top of the covered and not-covered tabs
    const waitingPeriodsSection = waitingPeriods && (
        <div className="flex flex-col text-sm">
            <div className="text-center text-sm font-bold">Waiting Periods</div>
            {waitingPeriods.intro && <p className="text-left text-content-secondary">{waitingPeriods.intro}</p>}
            <ul className="list-disc px-4 text-left text-content-secondary">
                {waitingPeriods.items.map((item, itemIndex) => (
                    <li key={itemIndex}>{item}</li>
                ))}
            </ul>
        </div>
    );

    return (
        <div ref={dialogRef} className="flex w-full flex-col gap-6 text-content-primary">
            <div className="flex flex-col gap-4">
                <Heading level="h2" className="mt-3 text-center text-xl font-semibold tracking-normal md:mt-1 lg:mt-0">
                    {Strings.COVERAGE_DETAILS}
                </Heading>
                <div className="flex justify-center" role="tablist">
                    <Button
                        className={`w-1/2 rounded-none text-sm font-bold hover:no-underline ${isCoveredTab ? "border-b-2 border-stroke-brand text-content-primary" : "border-b border-stroke-secondary text-content-secondary"}`}
                        aria-selected={isCoveredTab}
                        variant="link"
                        onClick={() => handleSetActiveTab("covered")}
                        aria-controls="tab1"
                        id="tab-button-1"
                        role="tab"
                    >
                        {Strings.COVERED_CTA}
                    </Button>
                    <Button
                        className={`w-1/2 rounded-none text-sm font-bold hover:no-underline ${!isCoveredTab ? "border-b-2 border-stroke-brand text-content-primary" : "border-b border-stroke-secondary text-content-secondary"}`}
                        aria-selected={!isCoveredTab}
                        variant="link"
                        onClick={() => handleSetActiveTab("notCovered")}
                        aria-controls="tab2"
                        id="tab-button-2"
                        role="tab"
                    >
                        {Strings.NOT_COVERED_CTA}
                    </Button>
                </div>
            </div>

            {activeTab === "covered" && (
                <div className="flex flex-col gap-6" id="tab1" role="tabpanel">
                    {/* waiting periods section  */}
                    {waitingPeriodsSection}

                    <div className="flex flex-col gap-3">
                        <SectionHeading icon={general.icon} headingLevel="h3" title={illnessCoverageEnabled ? general.illnessTitle : general.title} />
                        <div>
                            {combinedIncludes.map(inclusion => (
                                <TableRow
                                    key={inclusion.title}
                                    headingLevel="h3"
                                    title={inclusion.title}
                                    description={inclusion.description}
                                    badge={inclusion.affects ? getBreedSpecificBadge(inclusion.affects) : null}
                                />
                            ))}
                        </div>
                    </div>

                    {illnessCoverageEnabled && (
                        <>
                            <div className="flex flex-col items-center gap-4 md:flex-row">
                                <div className={cn(showCompellingStats ? "md:w-1/2" : "", "flex w-full flex-col gap-3 ")}>
                                    <SectionHeading icon={accident.icon} title={accident.title} />
                                    <div>
                                        {accident.includes.map(inclusion => (
                                            <TableRow
                                                key={inclusion.title}
                                                title={inclusion.title}
                                                badge={"affects" in inclusion && inclusion.affects ? getBreedSpecificBadge(inclusion.affects) : null}
                                            />
                                        ))}
                                    </div>
                                </div>
                                {showCompellingStats && (
                                    <div className="flex w-full items-center justify-center max-md:max-w-[296px] md:w-1/2">
                                        <Image src={OneInThree} alt={Strings.PTZ_US.STATS.EMERGENCY_VET_TREATMENT} />
                                    </div>
                                )}
                            </div>
                            <div className="grid auto-rows-min grid-cols-1 gap-4 md:grid-cols-2">
                                <div className="md:col-span-2">
                                    <SectionHeading
                                        icon={showRebrandTheme ? <ShotVaccineRebrand className="size-16" /> : <ShotVaccine className="size-16" />}
                                        title="Illness coverage includes:"
                                    />
                                </div>
                                <IllnessSubSection subSection={illnessSubSections.breedSpecific as SectionData} getBreedSpecificBadge={getBreedSpecificBadge} />
                                {isDentalCovered && (
                                    <IllnessSubSection subSection={illnessSubSections.dentalIllnesses as SectionData} getBreedSpecificBadge={getBreedSpecificBadge} />
                                )}
                                <IllnessSubSection subSection={illnessSubSections.cancer as SectionData} getBreedSpecificBadge={getBreedSpecificBadge} />
                                {showCompellingStats && (
                                    <div className="mx-auto flex w-full items-center justify-center max-md:max-w-[296px]">
                                        <Image src={EverySixSeconds} alt={Strings.PTZ_US.STATS.VET_BILL} />
                                    </div>
                                )}
                                <IllnessSubSection subSection={illnessSubSections.chronicConditions as SectionData} getBreedSpecificBadge={getBreedSpecificBadge} />
                                <IllnessSubSection subSection={illnessSubSections.orthopedicConditions as SectionData} getBreedSpecificBadge={getBreedSpecificBadge} />
                            </div>
                        </>
                    )}
                    {showBreedSpecificConditions && hasAnyBreedConditions && (
                        <div className="inline-flex w-fit items-center gap-1 text-xs text-content-warning">
                            <FontAwesomeIcon icon={faWarning} className="size-4" />
                            <div>
                                {"Likelihood for: " + getDisplayBreedName()}
                                <sup>1</sup>
                            </div>
                        </div>
                    )}

                    <div className="flex flex-col justify-center gap-4 md:flex-row">
                        {!illnessCoverageEnabled && showCompellingStats && (
                            <div className="mx-auto flex w-full items-center justify-center max-md:max-w-[296px] md:w-1/2">
                                <Image src={OneInThree} alt={Strings.PTZ_US.STATS.EMERGENCY_VET_TREATMENT} />
                            </div>
                        )}
                        <div className={`flex w-full flex-col gap-6 pb-6 ${illnessCoverageEnabled ? "" : "md:w-1/2"}`}>
                            {showTelehealth && (
                                <div className="flex flex-col items-center gap-3">
                                    <SectionHeading
                                        icon={showRebrandTheme ? <TelehealthIconRebrand className="size-16" /> : <TelehealthIcon className="size-16" />}
                                        title={Strings.PTZ_US.PET_HEALTH_HELPLINE}
                                    />
                                    <p className="max-w-[423px] text-center text-sm font-semibold text-content-secondary">
                                        Get immediate access to a 24/7 helpline for pet health, behavior, and wellness questions.
                                    </p>
                                </div>
                            )}

                            {samplePolicyUrl && (
                                <Button asChild className="mx-auto h-5 max-w-max py-0 text-center text-sm font-semibold underline" variant="link">
                                    <a href={samplePolicyUrl} target="_blank">
                                        {Strings.SAMPLE_POLICY_CTA}
                                    </a>
                                </Button>
                            )}
                            <Button
                                className="mx-auto h-5 max-w-max py-0 text-center text-sm font-semibold text-content-brand hover:no-underline"
                                variant="link"
                                endDecorator={<FontAwesomeIcon icon={faArrowRight} />}
                                onClick={() => handleSetActiveTab("notCovered")}
                                data-testid="bottom-coverage-not-covered-cta"
                            >
                                {Strings.NOT_COVERED_CTA}
                            </Button>

                            {showCompellingStats && (
                                <div className="mx-auto text-xs text-content-secondary">
                                    <sup>3</sup> &quot;Pet Insurance Statistics 2025&quot;{" "}
                                    <a href="https://www.forbes.com/advisor/pet-insurance/pet-insurance-statistics/" target="_blank" className="underline">
                                        Forbes Advisor
                                    </a>
                                    , Jan 2, 2025.‍‍
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {activeTab === "notCovered" && (
                <div className="flex flex-col items-center gap-6 pb-6 text-center text-sm" id="tab2" role="tabpanel">
                    <Heading level={"h3"} className="text-base font-semibold">
                        {Strings.COVERAGE_EXCLUSIONS_HEADER}
                    </Heading>
                    {waitingPeriodsSection}
                    {coverageExclusions
                        .filter(exclusion => !(exclusion.title === WAITING_PERIODS_TITLE && waitingPeriods))
                        .map((exclusion, index) => (
                            <div key={index}>
                                <Heading level="h3" className="text-sm font-semibold">
                                    {exclusion.title}
                                </Heading>
                                <p className="text-content-secondary">{exclusion.title === "Pre-existing Conditions" ? preExistingConditions : exclusion.description}</p>
                            </div>
                        ))}
                    <Button
                        className="h-5 py-0 text-center text-sm font-semibold text-content-brand hover:no-underline"
                        variant="link"
                        startDecorator={<FontAwesomeIcon icon={faArrowLeft} />}
                        onClick={() => handleSetActiveTab("covered")}
                        data-testid="bottom-coverage-covered-cta"
                    >
                        {Strings.COVERED_CTA}
                    </Button>
                </div>
            )}
        </div>
    );
};

export default FullCoverageDetailsContent;
